import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { poolAPI, dataAPI, seasonAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Check, Users, Trophy, Tv, MessageSquare, Lock } from 'lucide-react';

const PLANS = [
  { key: 'free', label: 'Free', price: 0, capacity: 8, features: ['Standings', 'Announcements', 'Rules'] },
  { key: 'basic', label: 'Basic', price: 47, capacity: 8, features: ['Everything in Free', 'Match Centre', 'Chat', 'Team Stats', 'Top Players'] },
  { key: 'starter', label: 'Starter', price: 97, capacity: 25, features: ['Everything in Basic', 'Up to 25 entrants'] },
  { key: 'standard', label: 'Standard', price: 197, capacity: 99, features: ['Everything in Basic', 'Up to 99 entrants'] },
  { key: 'pro', label: 'Pro', price: 397, capacity: 400, features: ['Everything in Basic', 'Up to 400 entrants'] },
];

export default function CreatePool() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan');
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seasonLocked, setSeasonLocked] = useState(false);
  useEffect(() => { document.title = "Create a Pool — Pools FC"; }, []);
  const [step, setStep] = useState(preselectedPlan ? 'details' : 'plan');

  useEffect(() => {
    const check = async () => {
      try { const res = await seasonAPI.status(); setSeasonLocked(res.data.season_locked); } catch (e) { console.error(e); }
    };
    check();
  }, []);

  const [form, setForm] = useState({
    name: '', description: '', season_id: '', plan: preselectedPlan || 'free',
    organizer_fee: '25 per entry', payment_email: '', pin: '', estimated_participants: '',
    entry_deadline: '2025-08-15T14:59',
    goals_prediction_enabled: true,
    prize_config: { first: 45, second: 25, third: 15, middle: 10, goals: 5, last_description: 'Cellar Dweller Prize' },
    scoring_config: {
      "1": { win: 2, draw: 1, loss: 0 },
      "2": { win: 4, draw: 2, loss: 0 },
      "3": { win: 6, draw: 3, loss: 0 },
      "4": { win: 8, draw: 4, loss: 0 },
      "5": { win: 10, draw: 5, loss: 0 },
    },
  });

  useEffect(() => {
    const load = async () => {
      const res = await dataAPI.getSeasons();
      setSeasons(res.data.seasons);
      if (res.data.seasons.length > 0) setForm(f => ({ ...f, season_id: res.data.seasons[0].id }));
    };
    load();
  }, []);

  // Auto-suggest plan based on estimated participants
  const suggestPlan = (num) => {
    if (num <= 8) return 'free';
    if (num <= 25) return 'starter';
    if (num <= 99) return 'standard';
    return 'pro';
  };

  const handleEstimateChange = (val) => {
    setForm(f => ({ ...f, estimated_participants: val, plan: suggestPlan(parseInt(val) || 0) }));
  };

  const prizeTotal = (form.prize_config.first || 0) + (form.prize_config.second || 0) + (form.prize_config.third || 0) + (form.prize_config.middle || 0) + (form.prize_config.goals || 0);
  const selectedPlan = PLANS.find(p => p.key === form.plan) || PLANS[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.season_id) { toast.error('Pool name and season required'); return; }
    if (!form.payment_email) { toast.error('Payment email is required'); return; }
    if (!form.pin || form.pin.length !== 4 || !/^\d{4}$/.test(form.pin)) { toast.error('A 4-digit PIN is required'); return; }
    if (Math.abs(prizeTotal - 100) > 0.01) { toast.error(`Prize percentages must total 100% (currently ${prizeTotal}%)`); return; }
    setLoading(true);
    try {
      const payload = { ...form, estimated_participants: parseInt(form.estimated_participants) || null };
      const res = await poolAPI.create(payload);
      toast.success('Pool created!');
      navigate(`/pools/${res.data.pool.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create pool');
    } finally { setLoading(false); }
  };

  const updatePrize = (key, val) => setForm(f => ({ ...f, prize_config: { ...f.prize_config, [key]: key === 'last_description' ? val : parseFloat(val) || 0 } }));

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="font-heading text-2xl font-bold mb-6">Create a Pool</h1>

      {seasonLocked ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Lock className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">Season is Locked</h3>
            <p className="text-muted-foreground text-sm mb-4">New pools cannot be created while the season is locked. Please contact the platform administrator.</p>
            <Button asChild variant="outline"><Link to="/pools">Back to Pools</Link></Button>
          </CardContent>
        </Card>
      ) : (
      <>

      {/* STEP 1: Plan Selection */}
      {step === 'plan' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>How many participants do you expect?</CardTitle>
              <CardDescription>This helps us recommend the right plan for your pool.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Estimated number of participants</Label>
                <Input type="number" value={form.estimated_participants} onChange={(e) => handleEstimateChange(e.target.value)} placeholder="e.g., 50" className="max-w-xs" />
              </div>
              {form.estimated_participants && parseInt(form.estimated_participants) > 0 && (
                <p className="text-sm">Based on your estimate of <strong>{form.estimated_participants} participants</strong>, we recommend the <strong className="text-[#5b3cc4]">{selectedPlan.label}</strong> plan.</p>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PLANS.map(plan => {
              const isSelected = form.plan === plan.key;
              const isRecommended = form.estimated_participants && suggestPlan(parseInt(form.estimated_participants) || 0) === plan.key;
              return (
                <button key={plan.key} onClick={() => setForm(f => ({ ...f, plan: plan.key }))}
                  className={`rounded-2xl border-2 p-5 text-left transition-all ${
                    isSelected ? 'border-[#5b3cc4] bg-[#f3f0ff]/30 ring-1 ring-[#5b3cc4]/30' : 'border-border hover:border-muted-foreground'
                  }`}>
                  {isRecommended && <Badge className="bg-[#5b3cc4] text-white text-[10px] mb-2">Recommended</Badge>}
                  <div className="font-heading text-2xl font-bold">{plan.price === 0 ? 'Free' : `$${plan.price}`}</div>
                  {plan.price > 0 && <div className="text-xs text-muted-foreground">per season</div>}
                  <div className="font-semibold text-sm mt-1">{plan.label}</div>
                  <div className="text-xs text-muted-foreground mb-3">Up to {plan.capacity} entrants</div>
                  <ul className="space-y-1">
                    {plan.features.map(f => (
                      <li key={f} className="text-xs flex items-center gap-1.5"><Check className="h-3 w-3 text-[#5b3cc4] flex-shrink-0" />{f}</li>
                    ))}
                  </ul>
                  {isSelected && <div className="mt-3 text-center"><Badge className="bg-[#5b3cc4] text-white">Selected</Badge></div>}
                </button>
              );
            })}
          </div>

          {form.plan !== 'free' && parseInt(form.estimated_participants || 0) <= 8 && (
            <Card className="border-[#5b3cc4]/20 bg-[#f3f0ff]/20">
              <CardContent className="pt-4">
                <h3 className="font-semibold text-sm mb-2 flex items-center gap-2"><Tv className="h-4 w-4 text-[#5b3cc4]" />Paid plans include:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-[#5b3cc4]" />Live EPL Fixtures</span>
                  <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-[#5b3cc4]" />League Table</span>
                  <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-[#5b3cc4]" />Team Statistics</span>
                  <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-[#5b3cc4]" />Top Players</span>
                  <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-[#5b3cc4]" />Player Profiles</span>
                  <span className="flex items-center gap-1.5"><MessageSquare className="h-3 w-3 text-[#5b3cc4]" />Pool Chat</span>
                </div>
              </CardContent>
            </Card>
          )}

          <Button size="lg" className="w-full bg-[#5b3cc4] hover:bg-[#4e33a8]" onClick={() => setStep('details')}>
            Continue with {selectedPlan.label} Plan {selectedPlan.price > 0 && `(${selectedPlan.price}/season)`}
          </Button>
        </div>
      )}

      {/* STEP 2: Pool Details */}
      {step === 'details' && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => setStep('plan')} className="text-sm text-[#5b3cc4] hover:underline">← Change plan</button>
            <Badge className="bg-[#f3f0ff] text-[#5b3cc4]">{selectedPlan.label} Plan — {selectedPlan.price === 0 ? 'Free' : `${selectedPlan.price}/season`} — Up to {selectedPlan.capacity} entrants</Badge>
          </div>

          <Card>
            <CardHeader><CardTitle>Pool Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Pool Name *</Label><Input data-testid="pool-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="My EPL Pool 2025" required /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Pool description..." /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Season</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.season_id} onChange={(e) => setForm({...form, season_id: e.target.value})}>
                    {seasons.map(s => <option key={s.id} value={s.id}>{s.year}/{s.year+1}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Pool PIN (4 digits) *</Label>
                  <Input type="text" maxLength={4} value={form.pin} onChange={(e) => setForm({...form, pin: e.target.value.replace(/\D/g, '').slice(0, 4)})} placeholder="e.g., 1234" required />
                  <p className="text-xs text-muted-foreground mt-1">Entrants must enter this PIN to join your pool. Share it with your invitees.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Entry Settings</CardTitle>
              <CardDescription className="flex items-start gap-2">
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#f3f0ff] text-[#5b3cc4] text-xs font-bold flex-shrink-0 mt-0.5">?</span>
                <span>Pool organizers often collect fees outside of the pool as prize amounts. Fill this section in to manage the fee that entrants will provide to the organizer.</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Entry Fee Details</Label>
                  <Input type="text" value={form.organizer_fee} onChange={(e) => setForm({...form, organizer_fee: e.target.value.replace(/\$/g, '')})} placeholder="e.g., 25 per entry" />
                  <p className="text-xs text-muted-foreground mt-1">The amount each entrant contributes to the pool. Collected offline by the organizer.</p>
                  <p className="text-[10px] text-muted-foreground">Please do not use $ here.</p>
                </div>
                <div><Label>Entry Deadline</Label><Input type="datetime-local" value={form.entry_deadline} onChange={(e) => setForm({...form, entry_deadline: e.target.value})} /></div>
              </div>
              <div>
                <Label>Payment Email (mandatory) *</Label>
                <Input type="email" value={form.payment_email} onChange={(e) => setForm({...form, payment_email: e.target.value})} placeholder="your-email@example.com" required />
                <p className="text-xs text-muted-foreground mt-1">Shown to entrants as where to send their pool contribution.</p>
              </div>
              <div className="flex items-center justify-between"><Label>Enable Goals Prediction</Label><Switch checked={form.goals_prediction_enabled} onCheckedChange={(v) => setForm({...form, goals_prediction_enabled: v})} /></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Prize Structure</CardTitle><CardDescription>Must total exactly 100%</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div><Label>1st Place (%)</Label><Input type="number" value={form.prize_config.first} onChange={(e) => updatePrize('first', e.target.value)} /></div>
                <div><Label>2nd Place (%)</Label><Input type="number" value={form.prize_config.second} onChange={(e) => updatePrize('second', e.target.value)} /></div>
                <div><Label>3rd Place (%)</Label><Input type="number" value={form.prize_config.third} onChange={(e) => updatePrize('third', e.target.value)} /></div>
                <div><Label>Middle of Pack (%)</Label><Input type="number" value={form.prize_config.middle} onChange={(e) => updatePrize('middle', e.target.value)} /></div>
                <div><Label>Goals Prediction (%)</Label><Input type="number" value={form.prize_config.goals} onChange={(e) => updatePrize('goals', e.target.value)} /></div>
              </div>
              <div><Label>Last Place Prize Description</Label><Input value={form.prize_config.last_description} onChange={(e) => updatePrize('last_description', e.target.value)} /></div>
              <p className={`text-xs font-semibold ${Math.abs(prizeTotal - 100) < 0.01 ? 'text-[#5b3cc4]' : 'text-destructive'}`}>Total: {prizeTotal}% {Math.abs(prizeTotal - 100) < 0.01 ? '(valid)' : '(must be 100%)'}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Scoring Multipliers</CardTitle><CardDescription>Customize points per tier. Lower tiers earn more by default.</CardDescription></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b">
                    <th className="px-3 py-2 text-left text-xs font-semibold">Tier</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold">Win Points</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold">Draw Points</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Loss Points</th>
                  </tr></thead>
                  <tbody>
                    {[1,2,3,4,5].map(tier => {
                      const t = form.scoring_config[String(tier)] || {};
                      const updateTier = (field, val) => setForm(f => ({
                        ...f, scoring_config: { ...f.scoring_config, [String(tier)]: { ...f.scoring_config[String(tier)], [field]: parseInt(val) || 0 } }
                      }));
                      return (
                        <tr key={tier} className="border-b">
                          <td className="px-3 py-2 font-semibold">Tier {tier}</td>
                          <td className="px-3 py-2 text-center"><Input type="number" value={t.win || 0} onChange={(e) => updateTier('win', e.target.value)} className="h-8 w-20 text-center mx-auto" /></td>
                          <td className="px-3 py-2 text-center"><Input type="number" value={t.draw || 0} onChange={(e) => updateTier('draw', e.target.value)} className="h-8 w-20 text-center mx-auto" /></td>
                          <td className="px-3 py-2 text-center text-muted-foreground">0</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Button data-testid="create-pool-submit" type="submit" size="lg" className="w-full bg-[#5b3cc4] hover:bg-[#4e33a8]" disabled={loading || Math.abs(prizeTotal - 100) > 0.01}>
            {loading ? 'Creating...' : `Create Pool (${selectedPlan.label} Plan)`}
          </Button>
        </form>
      )}
      </>
      )}
    </div>
  );
}
